if (typeof SubscriptionWidget !== 'function') {

	class SubscriptionWidget extends HTMLElement {

		constructor() {
			super();
			this.onChange = this.onChange.bind(this);
			this.onClick = this.onClick.bind(this);
			this.onVariantChange = this.onVariantChange.bind(this);
			this._otpTextAttr = 'data-subscription-otp-text';
		}

		connectedCallback() {
			this.addEventListener('change', this.onChange);
			this.addEventListener('click', this.onClick);
			this._productVariants = this.closest('product-page') && this.closest('product-page').querySelector('product-variants[data-main-product-variants]');
			if (this._productVariants) {
				this._productVariants.addEventListener('VARIANT_CHANGE', this.onVariantChange);
			}
			this.sortPlanOptions();
			this.restoreSelection();
			this.sync();
		}

		disconnectedCallback() {
			this.removeEventListener('change', this.onChange);
			this.removeEventListener('click', this.onClick);
			if (this._productVariants) {
				this._productVariants.removeEventListener('VARIANT_CHANGE', this.onVariantChange);
			}
		}

		get planSelect() {
			return this.querySelector('[data-subscription-plan]');
		}

		get sellingPlanInput() {
			return this.querySelector('[data-subscription-selling-plan]');
		}

		get selectedMode() {
			const checked = this.querySelector('[data-subscription-mode]:checked');
			if (checked) return checked.value;
			if (this.dataset.defaultSubscription === 'false') return 'otp';
			return 'subscription';
		}

		get selectedOption() {
			const select = this.planSelect;
			if (!select) return null;
			return select.options[select.selectedIndex] || null;
		}

		sortPlanOptions() {
			const select = this.planSelect;
			if (!select) return;
			const current = select.value;
			const options = Array.from(select.options).sort(function(a, b) {
				const weeksA = parseInt(a.dataset.weeks || '99', 10);
				const weeksB = parseInt(b.dataset.weeks || '99', 10);
				if (weeksA === weeksB) return 0;
				if (weeksA === 0) return 1;
				if (weeksB === 0) return -1;
				return weeksA - weeksB;
			});
			options.forEach(function(option) {
				select.appendChild(option);
			});
			if (current) select.value = current;
		}

		restoreSelection() {
			if (this._mode) {
				const radio = this.querySelector('[data-subscription-mode="' + this._mode + '"]');
				if (radio) radio.checked = true;
			}
			const select = this.planSelect;
			if (select && this._sellingPlanId) {
				const match = Array.from(select.options).some(function(option) {
					return option.value === String(this._sellingPlanId);
				}, this);
				if (match) {
					select.value = String(this._sellingPlanId);
				}
			}
		}

		onClick(event) {
			const card = event.target.closest('[data-subscription-card]');
			if (!card || !this.contains(card)) return;
			if (event.target.closest('select, option, a, button')) return;
			const radio = card.querySelector('[data-subscription-mode]');
			if (radio && !radio.checked) {
				radio.checked = true;
				radio.dispatchEvent(new Event('change', { bubbles: true }));
			}
		}

		onChange(event) {
			const target = event.target;
			if (target && target.matches('[data-subscription-plan]') && this.selectedMode !== 'subscription') {
				const subscriptionRadio = this.querySelector('[data-subscription-mode="subscription"]');
				if (subscriptionRadio) subscriptionRadio.checked = true;
			}
			this._mode = this.selectedMode;
			const option = this.selectedOption;
			this._sellingPlanId = option ? option.value : '';
			this.sync();
		}

		onVariantChange() {
			this.sortPlanOptions();
			this.restoreSelection();
			this.sync();
		}

		sync() {
			const mode = this.selectedMode;
			const option = this.selectedOption;
			const isSubscription = mode === 'subscription' && option;
			const sellingPlanId = isSubscription ? option.value : '';
			const priceCents = isSubscription
				? parseInt(option.dataset.price || '0', 10)
				: this.getOtpPriceCents();
			const compareCents = isSubscription
				? parseInt(option.dataset.compare || '0', 10)
				: this.getOtpCompareCents();

			this._mode = mode;
			this._sellingPlanId = sellingPlanId;

			this.updateCards(mode);
			this.updateWidgetPrices(option, isSubscription);
			this.updateSellingPlanInput(sellingPlanId, isSubscription);
			this.updateProductPrices(priceCents, compareCents, isSubscription);
			this.updateButtonTexts(isSubscription);

			document.dispatchEvent(new CustomEvent('subscription:updated', {
				detail: {
					mode: isSubscription ? 'subscription' : 'otp',
					sellingPlanId: sellingPlanId || null,
					priceCents: priceCents,
					compareCents: compareCents
				}
			}));
		}

		getOtpPriceCents() {
			const otp = this.querySelector('[data-otp-price]');
			return otp ? parseInt(otp.dataset.otpPrice || '0', 10) : 0;
		}

		getOtpCompareCents() {
			const otp = this.querySelector('[data-otp-price]');
			return otp ? parseInt(otp.dataset.otpCompare || '0', 10) : 0;
		}

		updateCards(mode) {
			this.querySelectorAll('.subscription-widget__card').forEach(function(card) {
				card.classList.toggle(
					'subscription-widget__card--selected',
					(mode === 'subscription' && card.classList.contains('subscription-widget__card--subscription')) ||
					(mode === 'otp' && card.classList.contains('subscription-widget__card--otp'))
				);
			});
		}

		updateWidgetPrices(option, isSubscription) {
			const priceEl = this.querySelector('[data-subscription-price-value]');
			const compareEl = this.querySelector('[data-subscription-compare]');
			const discountEl = this.querySelector('[data-subscription-discount]');
			const portionEl = this.querySelector('[data-subscription-portion]');
			if (!option || !isSubscription) {
				if (discountEl) discountEl.hidden = true;
				if (portionEl && option) {
					this.updatePortionElement(portionEl, parseInt(option.dataset.price || '0', 10));
				}
				return;
			}

			if (priceEl) priceEl.textContent = option.dataset.priceFormatted || '';

			const compareCents = parseInt(option.dataset.compare || '0', 10);
			const priceCents = parseInt(option.dataset.price || '0', 10);
			if (compareEl) {
				if (compareCents > priceCents && option.dataset.compareFormatted) {
					compareEl.textContent = option.dataset.compareFormatted;
					compareEl.hidden = false;
				} else {
					compareEl.hidden = true;
				}
			}

			const discount = parseInt(option.dataset.discount || '0', 10);
			if (discountEl) {
				if (discount > 0) {
					discountEl.textContent = '-' + discount + '%';
					discountEl.hidden = false;
				} else {
					discountEl.hidden = true;
				}
			}

			this.updatePortionElement(portionEl, priceCents);
		}

		getPortionsPerPackage() {
			return parseFloat(this.dataset.portionsPerPackage || '0');
		}

		formatPortionPrice(priceCents) {
			const portions = this.getPortionsPerPackage();
			if (!(portions > 0) || !(priceCents > 0)) return '';
			const portionCents = Math.round(priceCents / portions);
			const formatted = this.formatMoney(portionCents);
			const template = this.dataset.portionTemplate || '';
			if (template) return template.replace('__PRICE__', formatted);
			return formatted;
		}

		updatePortionElement(el, priceCents) {
			if (!el) return;
			const text = this.formatPortionPrice(priceCents);
			if (text) {
				el.textContent = text;
				el.hidden = false;
			} else {
				el.hidden = true;
			}
		}

		updateSellingPlanInput(sellingPlanId, isSubscription) {
			const input = this.sellingPlanInput;
			if (!input) return;
			if (isSubscription && sellingPlanId) {
				input.disabled = false;
				input.value = sellingPlanId;
			} else {
				input.value = '';
				input.disabled = true;
			}
		}

		updateProductPrices(priceCents, compareCents, isSubscription) {
			const formattedPrice = this.formatMoney(priceCents);
			const formattedCompare = compareCents > priceCents ? this.formatMoney(compareCents) : '';

			document.querySelectorAll('[data-subscription-price] .product-price--original').forEach((el) => {
				this.updateElementContent(el, '<span class="product-price--nur">nur</span> ' + formattedPrice);
			});

			document.querySelectorAll('[data-subscription-price] .product-price--compare').forEach((el) => {
				if (compareCents > priceCents && formattedCompare) {
					el.style.display = '';
					const hidden = el.querySelector('.visually-hidden');
					const hiddenHTML = hidden ? hidden.outerHTML + ' ' : '';
					const savingHTML = this.getSavingHTML(compareCents, priceCents);
					el.innerHTML = hiddenHTML +
						'<span class="product-price--compare-price" style="text-decoration: line-through;">' + formattedCompare + '</span>' +
						savingHTML;
				} else {
					const original = el.getAttribute('data-price-compare-formatted');
					if (original) {
						el.style.display = '';
						const hidden = el.querySelector('.visually-hidden');
						const hiddenHTML = hidden ? hidden.outerHTML + ' ' : '';
						el.innerHTML = hiddenHTML +
							'<span class="product-price--compare-price" style="text-decoration: line-through;">' + original + '</span>';
					} else {
						el.style.display = 'none';
					}
				}
			});

			document.querySelectorAll('[data-subscription-price] .product-price--unit').forEach((el) => {
				const unitPrice = el.getAttribute('data-price-unit-formatted');
				const unitCents = parseInt(el.getAttribute('data-price-unit') || '0', 10);
				const refValue = el.getAttribute('data-unit-reference-value') || '1';
				const refUnit = el.getAttribute('data-unit-reference-unit') || '';
				if (!refUnit) return;

				let displayPrice = unitPrice;
				if (isSubscription) {
					const originalPriceEl = el.closest('.product-price') && el.closest('.product-price').querySelector('.product-price--original');
					const originalPrice = parseFloat(originalPriceEl && originalPriceEl.getAttribute('data-price-original') || 0);
					if (originalPrice > 0 && priceCents > 0 && unitCents > 0) {
						displayPrice = this.formatMoney(Math.round(unitCents * (priceCents / originalPrice)));
					}
				}

				const unitText = displayPrice + ' / ' + (refValue !== '1' ? refValue + ' ' : '') + refUnit;
				this.updateElementContent(el, unitText);
			});

			document.querySelectorAll('[data-js-atc-price] .sticky-add-to-cart__price-original').forEach((el) => {
				el.textContent = formattedPrice;
			});
			document.querySelectorAll('[data-js-atc-price] .sticky-add-to-cart__price-compare').forEach((el) => {
				if (formattedCompare) {
					el.textContent = formattedCompare;
					el.style.display = '';
				} else {
					el.style.display = 'none';
				}
			});
		}

		getSavingHTML(compareCents, priceCents) {
			const template = this.dataset.savingTemplate;
			if (!template || compareCents <= priceCents) return '';
			const amount = this.formatMoney(compareCents - priceCents);
			return '<span class="product-price--compare-text">' + template.replace('__AMOUNT__', amount) + '</span>';
		}

		getAtcTextElements() {
			return [
				document.querySelector('[data-js-product-add-to-cart-text]'),
				document.querySelector('[data-js-atc] .button__text')
			].filter(Boolean);
		}

		updateButtonTexts(isSubscription) {
			const subscriptionText = this.dataset.atcText;
			this.getAtcTextElements().forEach((el) => {
				if (el.hasAttribute('data-has-custom-atc-text')) return;

				if (isSubscription && subscriptionText) {
					if (!el.hasAttribute(this._otpTextAttr)) {
						el.setAttribute(this._otpTextAttr, el.textContent.trim());
					}
					this.setButtonText(el, subscriptionText);
				} else {
					const otpText = el.getAttribute(this._otpTextAttr);
					if (otpText) this.setButtonText(el, otpText);
				}
			});
		}

		setButtonText(el, text) {
			const hidden = el.querySelector('.visually-hidden');
			if (hidden) {
				el.innerHTML = hidden.outerHTML + ' ' + text;
			} else {
				el.textContent = text;
			}
		}

		updateElementContent(el, content) {
			const hidden = el.querySelector('.visually-hidden');
			el.innerHTML = hidden ? hidden.outerHTML + ' ' + content : content;
		}

		formatMoney(cents) {
			const format = window.KROWN && window.KROWN.settings && window.KROWN.settings.shop_money_format;
			if (!format || typeof cents !== 'number') {
				return (cents / 100).toFixed(2).replace('.', ',') + ' €';
			}
			const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
			if (!match) return (cents / 100).toFixed(2).replace('.', ',') + ' €';
			const withDelimiters = function(n, prec, thousands, decimal) {
				thousands = thousands || ',';
				decimal = decimal || '.';
				const num = (n / 100).toFixed(prec);
				const parts = num.split('.');
				const intPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
				return intPart + (parts[1] ? decimal + parts[1] : '');
			};
			let value;
			switch (match[1]) {
				case 'amount': value = withDelimiters(cents, 2); break;
				case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
				case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
				case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
				case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' '); break;
				case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'"); break;
				default: value = (cents / 100).toFixed(2);
			}
			return format.replace(/\{\{\s*\w+\s*\}\}/, value);
		}

	}

	if (typeof customElements.get('subscription-widget') === 'undefined') {
		customElements.define('subscription-widget', SubscriptionWidget);
	}

}
